import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

/**
 * Single date filter input. Use for filtering by date (e.g. stage determination date).
 * value/onChange use dayjs or null; pass formatDateForApi(value) when sending to API (YYYY-MM-DD).
 */
export function formatDateForApi(value) {
  if (value == null) return null;
  const d = dayjs(value);
  return d.isValid() ? d.format('YYYY-MM-DD') : null;
}

export function DateFilterInput({ value, onChange, label = 'Date', size = 'small', sx = {}, slotProps = {} }) {
  const [open, setOpen] = useState(false);
  const userTextField = slotProps.textField ?? {};
  return (
    <Box sx={{ width: 165, minWidth: 165, flexShrink: 0, ...sx }}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          label={label}
          value={value ?? null}
          onChange={onChange}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          slotProps={{
            ...slotProps,
            textField: {
              size,
              ...userTextField,
              onClick: (e) => {
                setOpen(true);
                userTextField.onClick?.(e);
              },
            },
          }}
        />
      </LocalizationProvider>
    </Box>
  );
}

function isDateFilterActive(dateFilters) {
  return (dateFilters?.start != null && dayjs(dateFilters.start).isValid()) ||
    (dateFilters?.end != null && dayjs(dateFilters.end).isValid());
}

/**
 * Wrapper: "Date Filter" button that opens a popover with Start/End inputs, Apply, and Clear filter.
 * Applies both dates on Apply so the parent can refetch once. Button is visually active (contained) when filter is applied.
 */
export function DateFilterButton({ dateFilters = {}, onDateFiltersChange }) {
  const [anchor, setAnchor] = useState(null);
  const [pending, setPending] = useState({ start: null, end: null });
  const open = Boolean(anchor);
  const active = isDateFilterActive(dateFilters);

  useEffect(() => {
    if (open) setPending({ start: dateFilters?.start ?? null, end: dateFilters?.end ?? null });
  }, [open, dateFilters?.start, dateFilters?.end]);

  const handleApply = () => {
    onDateFiltersChange?.({ start: pending.start, end: pending.end });
    setAnchor(null);
  };

  const handleClear = () => {
    setPending({ start: null, end: null });
    if (active) onDateFiltersChange?.({ start: null, end: null });
    setAnchor(null);
  };

  return (
    <>
      <Button size="small" variant={active ? 'contained' : 'outlined'} onClick={(e) => setAnchor(e.currentTarget)} sx={{ textTransform: 'none' }}>
        Date Filter
      </Button>
      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 200 }}>
          <DateFilterInput label="Start" value={pending.start} onChange={(d) => setPending((p) => ({ ...p, start: d }))} />
          <DateFilterInput label="End" value={pending.end} onChange={(d) => setPending((p) => ({ ...p, end: d }))} />
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button size="small" onClick={handleClear}>Clear filter</Button>
            <Button size="small" variant="contained" onClick={handleApply}>Apply</Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
}

export { isDateFilterActive };

export default DateFilterInput;
